"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import React, { useEffect, useState } from "react"
import { useQuery } from "@apollo/client/react"
import useSessionAttributes from "../../../../../lib/auth/useSessionAttributes"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { cn } from "@/lib/utils"
import {
  AddGlyph,
  BusinessGlyph,
  CheckGlyph,
  KeyboardArrowDownGlyph,
  PeopleGlyph,
  PersonGlyph,
} from "../icons/glyphs"
import listUserOrganizations, {
  type ListUserOrganizationsQueryVariables,
} from "../../../../../graphql/queries/listUserOrganizations"
import { handleQueryError } from "../../../../../lib/logging"
import type { ListUserOrganizationsQueryData, UserOrganization } from "../types"
import { ZESTLIFE_BLUE, ZESTLIFE_ORG_LABEL } from "../zestlifeDropdownTokens"

/** MUI elevation 3 shadow (was `boxShadow: 3` on the org dropdown panel). */
const PANEL_SHADOW =
  "shadow-[0px_3px_3px_-2px_rgba(0,0,0,0.2),0px_3px_4px_0px_rgba(0,0,0,0.14),0px_1px_8px_0px_rgba(0,0,0,0.12)]"

/** Base of the old full-width MUI text Buttons (row + trigger). */
const BUTTON_BASE =
  "flex w-full cursor-pointer items-center border-0 bg-transparent font-roboto text-left transition-colors"

/** 20x20 soft-blue icon chip used in the menuEmbed variant. */
function EmbedIconChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] bg-[#E6F7FC]">
      {children}
    </span>
  )
}

function OrgRowButton({
  org,
  isCurrent,
  isPersonal,
  onSelect,
}: {
  org: UserOrganization
  isCurrent: boolean
  isPersonal: boolean
  onSelect: () => void
}) {
  const Icon = isPersonal ? PersonGlyph : BusinessGlyph
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`${BUTTON_BASE} gap-2 rounded px-3 py-2 hover:bg-[#fafafa]`}
    >
      <Icon size={16} style={{ color: "rgba(0, 0, 0, 0.54)" }} />
      <span className="min-w-0 flex-1 text-left">
        <span className="block overflow-hidden text-[16px] leading-4 font-medium text-ellipsis whitespace-nowrap text-[#092C3C]">
          {org.orgName}
        </span>
        <span className="block text-[12px] leading-[1.66] tracking-[0.03333em] text-[rgba(0,0,0,0.6)]">
          {isPersonal ? `Personal • ${org.role}` : org.role}
        </span>
      </span>
      {isCurrent && <CheckGlyph size={16} style={{ color: "#00B2FF" }} />}
    </button>
  )
}

function CompactOrgSwitcher({
  orgId,
  userId,
  menuEmbed = false,
}: {
  orgId?: string | number | null
  userId?: number | null
  /** Visual style for the account dropdown (dashed border, no grey strip). */
  menuEmbed?: boolean
}) {
  const { update } = useSessionAttributes()
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false)
  const router = useRouter()

  // Fetch all user organizations
  const {
    data: listUserOrganizationsData,
    loading,
    error,
  } = useQuery<ListUserOrganizationsQueryData, ListUserOrganizationsQueryVariables>(
    listUserOrganizations,
    {
      variables: {
        pUserId: userId ?? 0,
      },
      skip: userId == null,
    },
  )

  useEffect(() => {
    if (error) {
      handleQueryError(error)
    }
  }, [error])

  const userOrganizations: UserOrganization[] =
    listUserOrganizationsData?.listUserOrganizations?.edges
      ?.map((edge) => edge?.node)
      .filter((org): org is UserOrganization => Boolean(org)) || []

  // Determine current org from session orgId prop
  const currentOrganization = orgId
    ? userOrganizations.find((org: UserOrganization) => org?.orgId === orgId)
    : null

  const handleOrgSelect = async ({
    selectedOrgId,
    selectedOrgIsPersonal,
  }: {
    selectedOrgId: string
    selectedOrgIsPersonal: boolean
  }) => {
    setOrgSwitcherOpen(false)

    // Update session with new orgId
    try {
      if (selectedOrgId !== orgId) {
        await update({ orgId: selectedOrgId })
      } else if (selectedOrgIsPersonal !== currentOrganization?.isPersonal) {
        router.push(`/organization/${selectedOrgId}`)
      } else {
        router.push(`/dashboard`)
      }
    } catch (error) {
      console.error("Error updating session with orgId:", error)
      return
    }

    // Reload the page with updated session
    window.location.reload()
  }

  // Don't show while loading or if no orgs
  if (loading || !userOrganizations || userOrganizations.length === 0) {
    return null
  }

  const displayName = currentOrganization?.isPersonal
    ? "Personal Practice"
    : currentOrganization?.orgName || "Organization"

  const outerClasses = menuEmbed
    ? "border-b-[0.5px] border-solid border-b-[rgba(0,0,0,0.12)] bg-transparent px-[10px] py-2"
    : "bg-[#fafafa] px-4 py-3"

  const triggerClasses = cn(
    BUTTON_BASE,
    menuEmbed
      ? "rounded-[9px] border border-dashed border-[rgba(0,0,0,0.16)] bg-transparent px-2 py-[7px] hover:border-[rgba(0,0,0,0.24)] hover:bg-[#f7f7f7]"
      : "rounded-lg border border-solid border-[#e0e0e0] bg-white px-3 py-2 hover:border-[#bdbdbd]",
  )

  // If user has only one org, show "Add organization" option
  if (userOrganizations.length <= 1) {
    return (
      <div className={outerClasses}>
        <Link
          href="/onboarding/create-org"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <span
            className={cn(
              triggerClasses,
              "gap-2",
              menuEmbed ? "hover:bg-[#f7f7f7]" : "hover:bg-[#fafafa]",
            )}
          >
            {menuEmbed ? (
              <EmbedIconChip>
                <AddGlyph size={14} style={{ color: ZESTLIFE_BLUE }} />
              </EmbedIconChip>
            ) : (
              <AddGlyph size={16} style={{ color: "rgba(0, 0, 0, 0.6)" }} />
            )}
            <span
              className={cn(
                "flex-1 text-left font-medium",
                menuEmbed ? "text-[12px]" : "text-[16px] leading-4",
              )}
              style={{ color: menuEmbed ? ZESTLIFE_ORG_LABEL : "#092C3C" }}
            >
              Add organization
            </span>
            {menuEmbed ? (
              <span className="text-[16px] font-light text-[#bbb]">+</span>
            ) : null}
          </span>
        </Link>
      </div>
    )
  }

  // Show switcher if user has more than one org
  return (
    <div className={outerClasses}>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOrgSwitcherOpen(!orgSwitcherOpen)}
          className={cn(
            triggerClasses,
            "justify-between",
            !menuEmbed && "hover:bg-white",
          )}
        >
          <span className="flex items-center gap-2">
            {menuEmbed ? (
              <EmbedIconChip>
                <PeopleGlyph size={14} style={{ color: ZESTLIFE_BLUE }} />
              </EmbedIconChip>
            ) : (
              <PeopleGlyph size={16} style={{ color: "rgba(0, 0, 0, 0.6)" }} />
            )}
            <span className="text-[16px] leading-4 font-medium text-[#092C3C]">
              {displayName}
            </span>
          </span>
          <KeyboardArrowDownGlyph
            size={16}
            className={cn(
              "transition-transform duration-200",
              orgSwitcherOpen ? "rotate-180" : "rotate-0",
            )}
            style={{ color: "rgba(0, 0, 0, 0.6)" }}
          />
        </button>

        {/* Org Dropdown */}
        {orgSwitcherOpen && (
          <div
            className={`absolute top-full right-0 left-0 z-[1300] mt-1 overflow-hidden rounded-lg border border-solid border-[#eeeeee] bg-white ${PANEL_SHADOW}`}
          >
            {/* Personal Organization */}
            {userOrganizations
              .filter((org: UserOrganization) => org?.isPersonal)
              .map((org: UserOrganization) => (
                <OrgRowButton
                  key={org.orgId || "personal-org"}
                  org={org}
                  isPersonal
                  isCurrent={currentOrganization?.orgId === org.orgId}
                  onSelect={() =>
                    handleOrgSelect({
                      selectedOrgId: org.orgId || "",
                      selectedOrgIsPersonal: !!org.isPersonal,
                    })
                  }
                />
              ))}

            {/* Divider between personal and other orgs */}
            {userOrganizations.some((org) => !org.isPersonal) &&
              userOrganizations.some((org) => org.isPersonal) && (
                <hr className="m-0 h-px border-0 bg-[rgba(0,0,0,0.12)]" />
              )}

            {/* Other Organizations */}
            {userOrganizations
              .filter((org: UserOrganization) => !org?.isPersonal)
              .map((org: UserOrganization) => (
                <OrgRowButton
                  key={org.orgId || "org"}
                  org={org}
                  isPersonal={false}
                  isCurrent={currentOrganization?.orgId === org.orgId}
                  onSelect={() =>
                    handleOrgSelect({
                      selectedOrgId: org.orgId || "",
                      selectedOrgIsPersonal: !!org.isPersonal,
                    })
                  }
                />
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CompactOrgSwitcher
